import {
  type RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create_Args,
  type RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create_Options,
  useRentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type { ApiResponseErrorDto } from '@zg-rentals/ts-rental-manager-api-client';

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

const assertMultiplexAmenityCreateMutationArgs = (
  args: RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create_Args,
): void => {
  const { postData } = args;

  if (typeof postData !== 'object' || postData === null) {
    throw new TypeError(
      'useMultiplexAmenityCreate: `postData` is required (UserPropertyMultiplexAmenityCreateV2RequestDto).',
    );
  }

  const sessionId = postData.multiplexBuildingSessionId;
  const text = postData.text;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexAmenityCreate: `postData.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }

  if (typeof text !== 'string' || text.trim() === '') {
    throw new TypeError('useMultiplexAmenityCreate: `postData.text` is required on every call (non-empty string).');
  }
};

export type UseMultiplexAmenityCreateOptions = Omit<
  RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexAmenityCreateMutation = ReturnType<
  typeof useRentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create
>;

export type UseMultiplexAmenityCreateResult = Omit<
  BaseMultiplexAmenityCreateMutation,
  'mutate' | 'mutateAsync' | 'error'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create_Args,
    options?: Parameters<BaseMultiplexAmenityCreateMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexAmenityCreateMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create_Args,
    options?: Parameters<BaseMultiplexAmenityCreateMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexAmenityCreateMutation['mutateAsync']>;
  errors: ApiResponseErrorDto<unknown> | undefined;
};

/**
 * Multiplex additional-amenity create: wraps POST `/api/web/v2/users/properties/multiplex/amenities/create`.
 */
const useMultiplexAmenityCreate = (
  mutationOptions: UseMultiplexAmenityCreateOptions = {},
): UseMultiplexAmenityCreateResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexAmenityV2WebApiHandler_create',
        details: 'useMultiplexAmenityCreate',
        ...metaOverrides?.context,
      },
    },
  });

  const { error, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexAmenityCreateMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexAmenityCreateMutationArgs(args);
      return mutateAsync(args, options);
    },
    errors: toApiResponseErrorDto(error),
  };
};

export default useMultiplexAmenityCreate;
